import { ArrowRight, CalendarDays, Handshake } from "lucide-react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { EmptyState } from "@/components/empty-state"
import { ProgressBar } from "@/components/progress-bar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import type { DashboardContractRow } from "@/features/dashboard/api/types"
import { formatDate, formatNumber, kgToMt, percent } from "@/lib/format"
import { cn } from "@/lib/utils"
import { paths } from "@/routes/paths"

const AT_RISK_DAYS = 7
const AT_RISK_PCT = 90

type Tone = "info" | "warning" | "danger" | "success"

interface Status {
  tone: Tone
  daysLeft: number | null
}

function classify(row: DashboardContractRow): Status {
  const pct = percent(row.deliveredKg, row.contractedKg)
  if (!row.deadline) return { tone: "info", daysLeft: null }
  const due = new Date(row.deadline)
  if (Number.isNaN(due.getTime())) return { tone: "info", daysLeft: null }
  const daysLeft = Math.ceil(
    (due.getTime() - Date.now()) / (1000 * 60 * 60 * 24),
  )
  if (daysLeft < 0 && pct < 100) return { tone: "danger", daysLeft }
  if (daysLeft <= AT_RISK_DAYS && pct < AT_RISK_PCT)
    return { tone: "warning", daysLeft }
  if (pct >= 100) return { tone: "success", daysLeft }
  return { tone: "info", daysLeft }
}

const deadlineToneStyles: Record<Tone, string> = {
  info: "text-muted-foreground",
  success: "text-muted-foreground",
  warning: "text-amber-700 dark:text-amber-300",
  danger: "text-red-700 dark:text-red-300",
}

interface ContractRowProps {
  contract: DashboardContractRow
}

function ContractRow({ contract }: ContractRowProps) {
  const { t } = useTranslation("dashboard")
  const status = classify(contract)
  const fillPct = percent(contract.deliveredKg, contract.contractedKg)

  const deadlineHint = (() => {
    if (!contract.deadline) return undefined
    const date = formatDate(contract.deadline)
    if (status.tone === "danger" && status.daysLeft !== null) {
      return t("contractFocus.overdue", {
        count: Math.abs(status.daysLeft),
        date,
      })
    }
    if (status.tone === "warning" && status.daysLeft !== null) {
      return t("contractFocus.dueIn", { count: status.daysLeft, date })
    }
    return undefined
  })()

  const deadlineNode = contract.deadline ? (
    <span
      className={cn(
        "inline-flex items-center gap-1 tabular-nums",
        deadlineToneStyles[status.tone],
      )}
    >
      <CalendarDays className="size-3.5" />
      {formatDate(contract.deadline)}
    </span>
  ) : null

  return (
    <Link
      to={paths.contractDetail(contract.id)}
      className="group hover:bg-muted/30 block rounded-lg px-3 py-2.5 transition-colors"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <p className="truncate text-sm font-semibold">
            {contract.contractNumber}
          </p>
          {contract.clientName ? (
            <span className="text-muted-foreground truncate text-xs">
              · {contract.clientName}
            </span>
          ) : null}
          {contract.certifications?.map((cert) => (
            <Badge
              key={cert}
              variant="outline"
              className="bg-background h-4 px-1 text-[9px] font-normal"
            >
              {cert}
            </Badge>
          ))}
        </div>
        <ArrowRight className="text-muted-foreground/50 group-hover:text-foreground size-4 shrink-0 transition-colors" />
      </div>
      <div className="mt-2 flex items-center gap-3">
        <ProgressBar
          value={fillPct}
          tone={status.tone}
          className="h-1.5 flex-1"
        />
        <span className="shrink-0 text-xs font-semibold tabular-nums">
          {Math.round(fillPct)}%
        </span>
      </div>
      <div className="text-muted-foreground mt-1.5 flex items-center justify-between gap-3 text-xs">
        <span className="tabular-nums">
          {formatNumber(kgToMt(contract.deliveredKg), 1)} /{" "}
          {formatNumber(kgToMt(contract.contractedKg), 1)} MT
        </span>
        {deadlineHint ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span>{deadlineNode}</span>
            </TooltipTrigger>
            <TooltipContent side="top">{deadlineHint}</TooltipContent>
          </Tooltip>
        ) : (
          deadlineNode
        )}
      </div>
    </Link>
  )
}

interface ContractFocusProps {
  contracts: DashboardContractRow[]
}

export function ContractFocus({ contracts }: ContractFocusProps) {
  const { t } = useTranslation("dashboard")

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">
          {t("contractFocus.cardTitle")}
        </CardTitle>
        <Link
          to={paths.contracts}
          className="text-primary inline-flex items-center gap-1 text-xs font-medium hover:underline"
        >
          {t("contractFocus.viewAll")}
          <ArrowRight className="size-3.5" />
        </Link>
      </CardHeader>
      <CardContent>
        {contracts.length === 0 ? (
          <EmptyState
            icon={<Handshake className="size-6" />}
            title={t("contractFocus.empty.title")}
            description={t("contractFocus.empty.description")}
            action={
              <Button asChild size="sm">
                <Link to={paths.contractNew}>
                  {t("contractFocus.empty.cta")}
                </Link>
              </Button>
            }
            className="py-6"
          />
        ) : (
          <ul className="flex flex-col gap-4">
            {contracts.map((c) => (
              <li
                key={c.id}
                className="bg-card hover:shadow-xs rounded-lg border transition-shadow"
              >
                <ContractRow contract={c} />
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}
